import React, { useState, useEffect, useContext, useMemo } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Paper, 
  Autocomplete, 
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardMedia,
  useTheme,
  useMediaQuery,
  Stack,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, differenceInDays, addDays, isAfter, isBefore } from 'date-fns';
import { generateItinerary } from '../services/openaiService';
import ItineraryPage from './ItineraryPage';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupIcon from '@mui/icons-material/Group';
import InterestsIcon from '@mui/icons-material/Interests';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { TripForm } from '../components/TripForm';
import { useTrips } from '../hooks/useTrips';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getDestinationImage } from '../utils/imageUtils';
import { useAuth } from '../contexts/AuthContext';
import Login from '../components/Login';
import { AuthContext } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import TokenPurchaseDialog from '../components/TokenPurchaseDialog';
import { motion } from 'framer-motion';

const popularDestinations = [
  'Paris, France',
  'Tokyo, Japan',
  'New York, USA',
  'Rome, Italy',
  'Bali, Indonesia',
  'London, UK',
  'Sydney, Australia',
  'Barcelona, Spain',
  'Dubai, UAE',
  'Cancun, Mexico',
  'Santorini, Greece',
  'Kyoto, Japan',
  'Amsterdam, Netherlands',
  'Cape Town, South Africa',
  'Machu Picchu, Peru',
];

// Map of destinations to their background images
const destinationImages = {
  'Paris, France': 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34',
  'Tokyo, Japan': 'https://images.unsplash.com/photo-1536098561742-ca998e48cbcc',
  'New York, USA': 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9',
  'Rome, Italy': 'https://images.unsplash.com/photo-1552832230-c0197dd311b5',
  'Bali, Indonesia': 'https://images.unsplash.com/photo-1537996194471-e657df975ab4',
  'London, UK': 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad',
  'Sydney, Australia': 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9',
  'Barcelona, Spain': 'https://images.unsplash.com/photo-1539037116277-4db20889f2d4',
  'Dubai, UAE': 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c',
  'Cancun, Mexico': 'https://images.unsplash.com/photo-1552074284-5e84e180b029',
  'default': 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800'
};

const PlannerPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const { createTrip, loading } = useTrips();
  const [searchParams] = useSearchParams();
  const destinationParam = searchParams.get('destination');
  
  // Add loading state for the PlannerPage first
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  
  // Add loading messages at the top level
  const loadingMessages = [
    { icon: '🗺️', message: 'Finding points of interest...' },
    { icon: '🚗', message: 'Calculating travel distances...' },
    { icon: '🍽️', message: 'Discovering local restaurants...' },
    { icon: '🎭', message: 'Planning activities...' },
    { icon: '🏨', message: 'Finding accommodations...' },
    { icon: '📅', message: 'Organizing your schedule...' }
  ];

  // Move the loading message rotation effect to the top level
  useEffect(() => {
    let interval;
    if (isGenerating) {
      interval = setInterval(() => {
        setCurrentMessageIndex((prevIndex) => 
          prevIndex === loadingMessages.length - 1 ? 0 : prevIndex + 1
        );
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [isGenerating]);
  
  // Add state for the token purchase dialog
  const [tokenPurchaseDialogOpen, setTokenPurchaseDialogOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  
  // Function to safely parse dates from location state
  const getSafeDate = (dateString) => {
    if (!dateString) return null;
    
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? null : date;
    } catch (e) {
      console.error("Error parsing date:", e);
      return null;
    }
  };
  
  // Initialize form data with location state if available
  const initialFormData = useMemo(() => {
    const incoming = location.state?.tripDetails;
    
    if (incoming) {
      
      return {
        destination: incoming.destination || destinationParam || null,
        startDate: getSafeDate(incoming.startDate) || null,
        endDate: getSafeDate(incoming.endDate) || null,
        adults: incoming.adults || 2,
        children: incoming.children || 0,
        interests: Array.isArray(incoming.interests) 
          ? incoming.interests.join(', ') 
          : (incoming.interests || ''),
        budget: incoming.budget || 'medium'
      };
    }
    
    return {
      destination: destinationParam || null,
      startDate: null,
      endDate: null,
      adults: 2,
      children: 0,
      interests: '',
      budget: 'medium'
    };
  }, [location.state, destinationParam]);
  
  const [formData, setFormData] = useState(initialFormData);
  
  // Add loading state for the PlannerPage
  const [previousAuthState, setPreviousAuthState] = useState(null);
  const [pendingSubmission, setPendingSubmission] = useState(false);
  
  // Add a new state variable to store validated form data
  const [validatedFormData, setValidatedFormData] = useState(null);
  
  // Add a state variable to track if we're in the middle of a login-then-submit flow
  const [isSubmittingAfterLogin, setIsSubmittingAfterLogin] = useState(false);
  
  // Effect to update form data when URL parameters change
  useEffect(() => {
    if (destinationParam) {
      setFormData(prev => ({
        ...prev,
        destination: destinationParam
      }));
    }
  }, [destinationParam]);
  
  const [error, setError] = useState(null);
  const [itinerary, setItinerary] = useState(null);
  const [showItinerary, setShowItinerary] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Effect to log when currentUser changes
  useEffect(() => {
    console.log("Current user state updated:", user);
  }, [user]);
  
  // Effect to detect when user completes login and automatically submit the form
  useEffect(() => {
    // If the user just logged in and there's a pending submission with validated data
    if ((!previousAuthState && user) && pendingSubmission && validatedFormData) {
      // Ensure we have the validated form data in our form state
      setFormData(validatedFormData);
      
      // Reset pending submission state
      setPendingSubmission(false);
      
      // Set the flag that we're submitting after login
      setIsSubmittingAfterLogin(true);
      
      // Give a much longer delay to ensure auth token is available
      setTimeout(async () => {
        try {
          // Set generating state to true to show loading indicator
          setIsGenerating(true);
          
          // Add a timeout for the API call
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error("Request timed out. Please try again.")), 60000);
          });
          
          // Call generate itinerary directly
          const itineraryData = await Promise.race([
            generateItinerary(validatedFormData),
            timeoutPromise
          ]);
          
          // Handle successful result
          setItinerary(itineraryData);
          setShowItinerary(true);
          setIsGenerating(false);
          
          // Reset the post-login submission flag
          setIsSubmittingAfterLogin(false);
        } catch (err) {
          setIsGenerating(false); // Reset generating state on error
          if (err.tokenRequired || (err.message && err.message.includes('Insufficient tokens'))) {
            console.log('Insufficient tokens, opening purchase dialog');
            // Store a proper copy of form data with valid date objects
            const pendingData = { 
            ...validatedFormData,
            // Ensure dates are stored as Date objects
            startDate: validatedFormData.startDate ? new Date(validatedFormData.startDate) : null,
            endDate: validatedFormData.endDate ? new Date(validatedFormData.endDate) : null
            };
            setPendingFormData(pendingData);
            setTokenPurchaseDialogOpen(true);
          } else {
            console.error("Error generating itinerary after login:", err);
            setError(err.message || "Failed to generate itinerary");
            setIsSubmittingAfterLogin(false);
            alert(`Error: ${err.message || "Failed to generate itinerary. Please try again."}`);
          }
        }
      }, 1000); // Increased timeout to allow auth token to fully propagate
    }
    
    // Update previous auth state
    setPreviousAuthState(user);
  }, [user, pendingSubmission, validatedFormData]);
  
  // Effect to recover from page reloads or navigation during login flow
  useEffect(() => {
    // Only run this effect if user is logged in but we don't have pending submission
    if (user && !pendingSubmission && !isSubmittingAfterLogin && !showItinerary) {
      try {
        // Check if we have stored login data
        const storedLoginData = localStorage.getItem('roamZapLastLogin');
        if (storedLoginData) {
          const loginData = JSON.parse(storedLoginData);
          
          // Check if the stored data is recent (within last 5 minutes)
          const isRecent = (Date.now() - loginData.timestamp) < 5 * 60 * 1000;
          
          if (isRecent && loginData.hasPendingSubmission && loginData.formData) {
            // Restore the form data
            setFormData(loginData.formData);
            setValidatedFormData(loginData.formData);
            
            // Set submission flag
            setIsSubmittingAfterLogin(true);
            
            // Submit the form with a delay
            setTimeout(async () => {
              try {
                setIsGenerating(true);
                const itineraryData = await generateItinerary(loginData.formData);
                setItinerary(itineraryData);
                setShowItinerary(true);
                setIsGenerating(false);
                setIsSubmittingAfterLogin(false);
                // Clear the stored login data
                localStorage.removeItem('roamZapLastLogin');
              } catch (err) {
                  setIsGenerating(false); // Reset generating state on error
                  if (err.tokenRequired || (err.message && err.message.includes('Insufficient tokens'))) {
                  console.log('Insufficient tokens, opening purchase dialog');
                  // Store a proper copy of form data with valid date objects
                  const pendingData = { 
                  ...loginData.formData,
                    // Ensure dates are stored as Date objects
                    startDate: loginData.formData.startDate ? new Date(loginData.formData.startDate) : null,
                    endDate: loginData.formData.endDate ? new Date(loginData.formData.endDate) : null
                    };
                    setPendingFormData(pendingData);
                    setTokenPurchaseDialogOpen(true);
                  } else {
                    console.error("Error recovering submission:", err);
                    setError(err.message || "Failed to generate itinerary");
                    setIsSubmittingAfterLogin(false);
                  }
              }
            }, 1000);
          } else {
            // Clear expired login data
            localStorage.removeItem('roamZapLastLogin');
          }
        }
      } catch (err) {
        console.error("Error recovering from stored login:", err);
        localStorage.removeItem('roamZapLastLogin');
      }
    }
  }, [user, pendingSubmission, isSubmittingAfterLogin, showItinerary]);
  
  // Handle background image for destination - both from predefined list and custom entries
  const getBackgroundImage = () => {
    if (!formData.destination) return destinationImages.default;
    
    // Check if we have an exact match in our predefined destination images
    if (destinationImages[formData.destination]) {
      return destinationImages[formData.destination];
    } 
    
    // For custom destinations, try to find a partial match
    const destinationLower = formData.destination.toLowerCase();
    for (const [key, value] of Object.entries(destinationImages)) {
      if (key === 'default') continue;
      if (key.toLowerCase().includes(destinationLower) || destinationLower.includes(key.toLowerCase())) {
        return value;
      }
    }
    
    // If no match, return default background
    return destinationImages.default;
  };
  
  const backgroundImage = getBackgroundImage();
  
  const handleDestinationChange = (event, newValue) => {
    setFormData({ ...formData, destination: newValue });
  };
  
  const handleDateChange = (field, newValue) => {
    setFormData({ ...formData, [field]: newValue });
    
    // If setting start date and it's after end date, update end date
    if (field === 'startDate' && formData.endDate && newValue > formData.endDate) {
      setFormData({ 
        ...formData, 
        startDate: newValue, 
        endDate: addDays(newValue, 3) 
      });
    }
    
    // If setting end date and it's before start date, update start date
    if (field === 'endDate' && formData.startDate && newValue < formData.startDate) {
      setFormData({ 
        ...formData, 
        endDate: newValue, 
        startDate: addDays(newValue, -3) 
      });
    }
  };
  
  const handleNumberChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };
  
  const handleInterestsChange = (event) => {
    setFormData({ ...formData, interests: event.target.value });
  };
  
  const handleBudgetChange = (event) => {
    setFormData({ ...formData, budget: event.target.value });
  };
  
  const handleFormSubmit = async (submittedFormData) => {
    setError(null);
    
    // Make a deep copy of the form data to avoid reference issues
    const formDataCopy = JSON.parse(JSON.stringify(submittedFormData));
    
    // Save the form data first so we have it after login
    setFormData(formDataCopy);
    
    // Basic validation before proceeding
    if (!formDataCopy.destination || !formDataCopy.startDate || !formDataCopy.endDate) {
      setError("Please fill in all required fields (destination, start date, and end date)");
      return;
    }
    
    // Store validated form data for use after login - using deep copy to avoid reference issues
    setValidatedFormData(formDataCopy);
    
    // Check if user is logged in
    if (!user) {
      setPendingSubmission(true);
      setLoginDialogOpen(true);
      return;
    }
    
    // Otherwise, proceed with submission
    try {
      // Set generating state to true to show loading indicator
      setIsGenerating(true);
      
      // Add a timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Request timed out. Please try again.")), 60000);
      });
      
      // Race the API call against the timeout
      const itineraryData = await Promise.race([
        generateItinerary(formDataCopy),
        timeoutPromise
      ]);
      
      // If we reach here, we got valid data
      setItinerary(itineraryData);
      setShowItinerary(true);
      setIsGenerating(false);
    } catch (err) {
      console.error("Error in form submission:", err);
      setIsGenerating(false); // Reset generating state on error
      // Check if this is a token-related error
      if (err.tokenRequired || (err.message && err.message.includes('Insufficient tokens'))) {
        console.log('Insufficient tokens, opening purchase dialog');
        
        // Store a proper copy of form data with valid date objects
        const pendingData = { 
          ...formDataCopy,
          // Ensure dates are stored as Date objects
          startDate: formDataCopy.startDate ? new Date(formDataCopy.startDate) : null,
          endDate: formDataCopy.endDate ? new Date(formDataCopy.endDate) : null
        };
        
        setPendingFormData(pendingData);
        setTokenPurchaseDialogOpen(true);
      } else {
        setError(err.message || "Failed to generate itinerary");
        alert(`Error: ${err.message || "Failed to generate itinerary. Please try again."}`);
      }
      
      setIsGenerating(false); // Reset generating state on error
    }
  };
  
  const handleLoginSuccess = (user) => {
    // Store the user info immediately after successful login
    localStorage.setItem('roamZapLastLogin', JSON.stringify({
      timestamp: Date.now(),
      formData: validatedFormData,
      hasPendingSubmission: true
    }));
    
    // Close the dialog immediately - the effect will handle the submission
    setTimeout(() => {
      setLoginDialogOpen(false);
    }, 100);
  };
  
  const handleCloseLoginDialog = () => {
    // If user manually closes the dialog, we should reset pendingSubmission
    setPendingSubmission(false);
    setLoginDialogOpen(false);
  };
  
  const resetForm = () => {
    setFormData({
      destination: null,
      startDate: null,
      endDate: null,
      adults: 2,
      children: 0,
      interests: '',
      budget: 'medium'
    });
    setItinerary(null);
    setShowItinerary(false);
    setError(null);
    setIsGenerating(false);
  };
  
  // Handler for token purchase success
  const handleTokenPurchase = (tokenPackage) => {
    console.log(`Purchased ${tokenPackage.tokens} tokens for $${tokenPackage.price}`);
    // In a real implementation, you would update the user's token balance via an API call
    
    // Close the token purchase dialog
    setTokenPurchaseDialogOpen(false);
    
    // If we have pending form data, retry the submission
    if (pendingFormData) {
      // Ensure date objects are properly created before submission
      const formDataWithDates = {
        ...pendingFormData,
        startDate: pendingFormData.startDate ? new Date(pendingFormData.startDate) : null,
        endDate: pendingFormData.endDate ? new Date(pendingFormData.endDate) : null
      };
      
      setTimeout(() => {
        handleFormSubmit(formDataWithDates);
        setPendingFormData(null);
      }, 1000);
    }
  };
  
  // If showing itinerary, render the ItineraryPage component
  if (showItinerary && itinerary) {
    try {
      // Ensure dates are passed as valid Date objects
      const sanitizedItinerary = {
        ...itinerary,
        startDate: formData.startDate ? new Date(formData.startDate) : new Date(),
        endDate: formData.endDate ? new Date(formData.endDate) : new Date(),
        days: Array.isArray(itinerary.days) ? itinerary.days.map(day => ({
          ...day,
          date: day.date ? new Date(day.date) : new Date(),
          activities: Array.isArray(day.activities) ? day.activities : []
        })) : []
      };
      
      return (
        <ItineraryPage 
          initialTripDetails={{
            destination: formData.destination,
            startDate: formData.startDate ? new Date(formData.startDate) : new Date(),
            endDate: formData.endDate ? new Date(formData.endDate) : new Date(),
            travelers: formData.adults + formData.children,
            adults: formData.adults,
            children: formData.children,
            budget: formData.budget,
            interests: formData.interests ? formData.interests.split(',').map(i => i.trim()) : []
          }}
          itinerary={sanitizedItinerary}
          onEditTrip={() => {
            console.log("Returning to planner");
            setShowItinerary(false);
            // Make sure loading state is reset when returning to planner
            setIsGenerating(false);
          }}
        />
      );
    } catch (err) {
      console.error("Error rendering itinerary:", err);
      // If there's an error rendering the itinerary, reset to form view
      setShowItinerary(false);
      setIsGenerating(false);
      setError("There was an error displaying your itinerary. Please try again.");
      return null;
    }
  }
  
  // Show loading state when generating itinerary
  if (isGenerating) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: { xs: 'calc(100vh - 120px)', sm: 'calc(100vh - 64px)' },
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4)), url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        px: { xs: 2, sm: 4 },
        textAlign: 'center'
      }}>
        <Box
          component={motion.div}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: '100%',
            width: '400px',
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(10px)',
            p: { xs: 3, sm: 4 },
            borderRadius: 2
          }}
        >
          <CircularProgress size={{ xs: 40, sm: 48 }} sx={{ color: 'white' }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mt: 3, 
                color: 'white',
                fontSize: { xs: '0.9rem', sm: '0.9rem' },
                px: { xs: 1, sm: 2 },
                wordBreak: 'break-word'
              }}
            >
              Creating Your Personalized Itinerary To
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                mt: 0.5, 
                color: 'white',
                fontSize: { xs: '0.9rem', sm: '1.1rem' },
                px: { xs: 1, sm: 2 },
                wordBreak: 'break-word',
                fontWeight: 700
              }}
            >
              {formData.destination}
            </Typography>
          </Box>

          <Box
            component={motion.div}
            key={currentMessageIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'white',
                fontSize: { xs: '1.1rem', sm: '1.5rem' }
              }}
            >
              {loadingMessages[currentMessageIndex].icon}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: { xs: '0.8rem', sm: '0.9rem' }
              }}
            >
              {loadingMessages[currentMessageIndex].message}
            </Typography>
          </Box>

          <Typography 
            variant="caption" 
            sx={{ 
              mt: 3,
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              maxWidth: '280px'
            }}
          >
            This usually takes about a minute. We're making sure every detail is perfect!
          </Typography>
        </Box>
      </Box>
    );
  }
  
  return (
    <>
      <Box sx={{ 
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.3)), url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: { xs: 'calc(100vh - 56px)', sm: '100vh' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pt: { xs: 6, sm: 8 },
        pb: { xs: 4, sm: 6 }
      }}>
        <Container maxWidth="lg">
          <Box 
            sx={{
              width: '100%',
              mx: 'auto'
            }}
          >
            <TripForm onSubmit={handleFormSubmit} initialValues={formData} />
          </Box>
        </Container>
      </Box>
      
      {/* Login Dialog */}
      <Login 
        open={loginDialogOpen} 
        onClose={handleCloseLoginDialog} 
        onSuccess={handleLoginSuccess}
        message="Please sign in to generate your personalized travel itinerary. This allows us to save your trips and provide a better experience."
      />
      
      {/* Token Purchase Dialog */}
      <TokenPurchaseDialog 
        open={tokenPurchaseDialogOpen}
        onClose={() => {
          setTokenPurchaseDialogOpen(false);
          // Don't reset the pendingFormData when user cancels
          // This way the form data is preserved if they want to try again
        }}
        onPurchase={handleTokenPurchase}
      />
    </>
  );
};

export default PlannerPage; 